import { gql } from '@apollo/client';

/**
 * The call queue (card 21, on card 16's API and card 21's summary):
 * CRM_CONTACT_QUERY_FRONTEND.md §18.6 and §25 in ilbp-backend.
 */

/**
 * How many people the queue holds now, by phase, and why the rest of the
 * filter is not in it. One root field; asked when the settings change and when
 * a caller comes back to them, never on a timer.
 */
export const CRM_CALL_QUEUE_SUMMARY = gql`
  query CrmCallQueueSummary($filter: CrmContactFilter, $followUps: CrmCallQueueFollowUps, $rest: Int) {
    crmCallQueueSummary(filter: $filter, followUps: $followUps, skipCalledWithinHours: $rest) {
      inQueue
      followUpsDue
      neverCalled
      notCalledRecently
      resting
      followUpsLater
      beingCalled
      cannotCall
      restHours
    }
  }
`;

/**
 * The next person, claimed for this caller. `after` is the last person's
 * cursor: skip them, or move on once their call is logged. It releases the
 * caller's previous claim itself. The contact carries what the caller sees
 * while dialling; the rest is one click away on their page.
 */
export const CRM_CALL_QUEUE_NEXT = gql`
  mutation CrmCallQueueNext($filter: CrmContactFilter, $followUps: CrmCallQueueFollowUps, $rest: Int, $after: String) {
    crmCallQueueNext(filter: $filter, followUps: $followUps, skipCalledWithinHours: $rest, after: $after) {
      reason
      cursor
      claimExpiresAt
      followUp {
        id
        occurredAt
        followUpAt
        outcome
        notes
        admin {
          id
          firstName
          lastName
        }
      }
      contact {
        id
        fullName
        firstName
        lastName
        primaryEmail
        primaryPhone
        doNotCall
        doNotContact
        canCall
        countryCode
        region
        city
        supportLevel
        lifecycleStage
        callCount
        lastCalledAt
        lastContactedAt
        source
        phones {
          id
          type
          e164
          rawDisplay
          isPrimary
          isBad
        }
        tags {
          tagId
          name
          color
        }
        notes {
          id
          body
          source
          createdAt
        }
        owner {
          id
          firstName
          lastName
        }
        account {
          id
        }
      }
    }
  }
`;

/** Frees this caller's person at once: they stopped calling. Also sent when the screen closes. */
export const CRM_CALL_QUEUE_RELEASE = gql`
  mutation CrmCallQueueRelease {
    crmCallQueueRelease
  }
`;

/** The person's last few calls, beside the form: what was said last time. */
export const CRM_QUEUE_RECENT_CALLS = gql`
  query CrmQueueRecentCalls($contactId: Int!) {
    crmContactTimeline(contactId: $contactId, kinds: [CALL], take: 3) {
      items {
        id
        kind
        occurredAt
        title
        body
        call {
          callId
          outcome
          method
          direction
          durationSec
          followUpAt
          setDoNotCall
          admin {
            id
            firstName
            lastName
          }
          author {
            id
            fullName
          }
        }
      }
    }
  }
`;
